import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import "./dispatcher.css";
import "./common-view.css";
import ClientSearch from "./client-search";
import useAuthSession from "../hooks/useAuthSession";
import { BACKEND_BASE_URL } from "../config/appUrls";
import { clearAuthSession, hasValidPlatformAccess } from "../utils/auth";

function calculateHash(obj) {
  const str = JSON.stringify(obj, Object.keys(obj).sort());
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    const chr = str.charCodeAt(i);
    hash = (hash << 5) - hash + chr;
    hash |= 0;
  }
  return hash;
}

const DIS_PAGE_SIZE = 5;

const JOB_TYPE = ["delivery", "taxi", "bus", "pickup", "transport"];
const DIS_STATUS = [
  "requested",
  "quoted",
  "accepted",
  "assigned",
  "enroute_pickup",
  "picked_up",
  "enroute_dropoff",
  "completed",
  "cancelled",
  "failed"
];

export default function Dispatcher() {
  const { t, i18n } = useTranslation();
  const auth = useAuthSession();
  const { token, clientId, isAdmin, language } = auth;
  const [selectedClient, setSelectedClient] = useState(null);
  const [targetClientId, setTargetClientId] = useState(clientId || "");

  const [err, setErr] = useState("");
  const [disJobs, setDisJobs] = useState([]);
  const [editingDisJobs, setEditingDisJobs] = useState(null);
  const [savingJob, setSavingJob] = useState(false);

  const [disDriverName, setDisDriverName] = useState("");
  const [disDriverPhone, setDisDriverPhone] = useState("");
  const [disStatus, setDisStatus] = useState("assigned");
  const [disEtaMins, setDisEtaMins] = useState(0);
  const [disTracking, setDisTracking] = useState("{}");
  const [disProof, setDisProof] = useState("{}");
  const [jobType, setJobType] = useState("delivery");
  const [jobSource, setJobSource] = useState("");
  const [jobOrderSummary, setJobOrderSummary] = useState("");
  const [jobStatus, setJobStatus] = useState("assigned");
  const [jobConstraints, setJobConstraints] = useState("{}");
  const [jobPricing, setJobPricing] = useState("{}");
  const [jobSecurity, setJobSecurity] = useState("{}");

  const [jobToDelete, setJobToDelete] = useState(null);
  const [deletingJob, setDeletingJob] = useState(false);
  const [confirmOverwrite, setConfirmOverwrite] = useState(false);
  const [disJobsPage, setDisJobsPage] = useState(0);
  const [validationMessage, setValidationMessage] = useState("");
  const [originalHash, setOriginalHash] = useState(null);
  const [pendingConfirmAction, setPendingConfirmAction] = useState(null);

  const currentLocale = useMemo(() => {
    const lang = (i18n.language || language || "en").toLowerCase();
    return lang.startsWith("es") ? "es-ES" : "en-US";
  }, [i18n.language, language]);

  const handleClientSelect = useCallback(client => {
    setSelectedClient(client);
    setTargetClientId(client.client_id);
  }, []);

  const hasPlatformAccess = useMemo(() => {
    return hasValidPlatformAccess(selectedClient);
  }, [selectedClient]);

  useEffect(() => {
    if (!selectedClient) return;
    if (!hasPlatformAccess) {
      setValidationMessage(t("dashboard.subscriptionRequiredDesc"));
      window.location.href = "/dashboard?tab=onboarding";
    }
  }, [hasPlatformAccess, selectedClient, t]);

  useEffect(() => {
    if (!isAdmin && clientId) {
      setTargetClientId(clientId);
    }
  }, [isAdmin, clientId]);

  const base = useMemo(() => BACKEND_BASE_URL, []);
  const isCreateMode = editingDisJobs && editingDisJobs.__mode === "create";
  const isEditMode = editingDisJobs && editingDisJobs.assignment_id;

  const paginatedJobs = disJobs.slice(
    disJobsPage * DIS_PAGE_SIZE,
    (disJobsPage + 1) * DIS_PAGE_SIZE
  );

  const hasNextJobPage = (disJobsPage + 1) * DIS_PAGE_SIZE < disJobs.length;
  const hasPrevJobPage = disJobsPage > 0;

  useEffect(() => {
    setDisJobsPage(0);
  }, [disJobs]);

  function startCreateJob() {
    if (!targetClientId) {
      setValidationMessage(t("commonLabel.selectClientFirst", "Please select a client first."));
      return;
    }
    setEditingDisJobs({ __mode: "create" });
    setJobType("delivery");
    setDisTracking("");
    setDisProof("");
    setDisDriverName("");
    setDisDriverPhone("");
    setDisStatus("assigned");
    setDisEtaMins(0);
    setJobSource("");
    setJobOrderSummary("");
    setJobStatus("requested");
    setJobConstraints("{}");
    setJobPricing("{}");
    setJobSecurity("{}");
  }

  function cancelEditJob() {
    setEditingDisJobs(null);
  }

  async function performSave({ mode = "save" } = {}) {
    try {
      setSavingJob(true);
      const isAdd = mode === "add";

      const body = {
        client_id: targetClientId,
        assignment_id: isAdd ? null : editingDisJobs?.assignment_id || null,
        driver_name: disDriverName.trim() || "",
        driver_phone: disDriverPhone.trim() || "",
        source: jobSource.trim() || "",
        tracking: disTracking,
        proof: disProof,
        status: disStatus,
        eta_minutes: disEtaMins || 0,
        job_type: jobType,
        order_summary: jobOrderSummary,
        jstatus: jobStatus,
        constraints: jobConstraints,
        pricing: jobPricing,
        security: jobSecurity
      };

      const res = await fetch(`${base}/api/dispatchJobs`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(body)
      });

      if (!res.ok) throw new Error("Failed to save DIS");

      setEditingDisJobs(null);
      await loadJobMsgs(targetClientId);
    } finally {
      setSavingJob(false);
    }
  }

  async function saveJob() {
    if (!targetClientId) return;

    if (!isEditMode) {
      await performSave();
      return;
    }

    const currentSnapshot = buildCurrentSnapshot();
    const newHash = calculateHash(currentSnapshot);

    if (newHash === originalHash) return;

    const onlyStatusOrResolutionChanged =
      currentSnapshot.job_type === editingDisJobs.job_type &&
      currentSnapshot.order_summary === editingDisJobs.order_summary &&
      currentSnapshot.driver_name === editingDisJobs.driver_name &&
      currentSnapshot.driver_phone === editingDisJobs.driver_phone &&
      currentSnapshot.source === editingDisJobs.source;

    if (onlyStatusOrResolutionChanged) {
      await performSave();
      return;
    }

    setPendingConfirmAction("save");
    setConfirmOverwrite(true);
  }

  const loadJobMsgs = useCallback(
    async clientId => {
      const res = await fetch(`${base}/api/dispatchJobs/search`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json"
        },
        body: JSON.stringify({ client_id: clientId })
      });

      if (!res.ok) {
        if (res.status === 401 || res.status === 403) {
          clearAuthSession();
          throw new Error("Invalid or expired token");
        }
        throw new Error("Failed to load DIS codes");
      }

      const data = await res.json();
      setDisJobs(Array.isArray(data) ? data : []);
    },
    [base, token]
  );

  useEffect(() => {
    if (!token || !targetClientId) return;
    loadJobMsgs(targetClientId).catch(e => setErr(e.message));
  }, [token, targetClientId, loadJobMsgs]);

  function getJobTypeLabel(type) {
    return t(`dispatcher.jobTypes.${type}`, type);
  }

  function getJobStatusLabel(state) {
    return t(`dispatcher.status.${state}`, state);
  }

  function startEditJob(dis) {
    setEditingDisJobs(dis);
    setDisDriverName(dis.driver_name || "");
    setDisDriverPhone(dis.driver_phone || "");
    setDisStatus(dis.dstatus || "assigned");
    setDisEtaMins(dis.eta_minutes || 0);
    setDisTracking(dis.tracking || "");
    setDisProof(dis.proof || "");
    setJobType(dis.job_type);
    setJobSource(dis.source || "");
    setJobOrderSummary(dis.order_summary || "");
    setJobStatus(dis.jstatus || "requested");
    setJobConstraints(JSON.stringify(dis.constraints || {}, null, 2));
    setJobPricing(JSON.stringify(dis.pricing || {}, null, 2));
    setJobSecurity(JSON.stringify(dis.security || {}, null, 2));

    const snapshot = {
      job_type: dis.job_type || "",
      order_summary: dis.order_summary || "",
      driver_name: dis.customer_name || "",
      driver_phone: dis.driver_phone || "",
      source: dis.source || ""
    };

    setOriginalHash(calculateHash(snapshot));
  }

  function buildCurrentSnapshot() {
    return {
      driver_name: disDriverName.trim(),
      driver_phone: disDriverPhone.trim(),
      dstatus: disStatus,
      eta_minutes: disEtaMins,
      tracking: disTracking,
      proof: disProof,
      job_type: jobType,
      source: jobSource.trim(),
      order_summary: jobOrderSummary,
      jstatus: jobStatus,
      constraints: jobConstraints,
      pricing: jobPricing,
      security: jobSecurity
    };
  }

  async function confirmDeleteJob() {
    if (!jobToDelete) return;
    setDeletingJob(true);

    try {
      const res = await fetch(`${base}/api/dispatchJobs`, {
        method: "DELETE",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json"
        },
        body: JSON.stringify({
          assignment_id: jobToDelete.assignment_id,
          client_id: targetClientId
        })
      });

      if (!res.ok) {
        if (res.status === 401 || res.status === 403) {
          clearAuthSession();
          throw new Error("Invalid or expired token");
        }
        throw new Error("Delete failed");
      }

      setDisJobs(prev => prev.filter(q => q.assignment_id !== jobToDelete.assignment_id));
      setJobToDelete(null);
    } finally {
      setDeletingJob(false);
    }
  }

  const handleAdd = async () => {
    if (!targetClientId) {
      setValidationMessage(t("commonLabel.selectClientFirst", "Please select a client first."));
      return;
    }

    const currentSnapshot = buildCurrentSnapshot();
    const newHash = calculateHash(currentSnapshot);

    if (isEditMode && newHash !== originalHash) {
      setPendingConfirmAction("add");
      setConfirmOverwrite(true);
      return;
    }

    setDisStatus("assigned");
    setDisEtaMins(0);
    setJobOrderSummary("");
    setJobStatus("requested");
    setJobConstraints("{}");
    setJobPricing("{}");
    setJobSecurity("{}");
    await performSave({ mode: "add" });
  };

  function formatStatusDate(status, createdAt, updatedAt) {
    let dateSource = status === "open" ? createdAt : (updatedAt || createdAt);
    if (!dateSource) return "";
    return new Date(dateSource).toLocaleDateString(currentLocale, {
      year: "numeric",
      month: "long",
      day: "numeric"
    });
  }

  function renderJobForm() {
    return (
      <div className="job-card editing" onClick={e => e.stopPropagation()}>
        <div className="job-title">
          ✏️ {isCreateMode ? t("dispatcher.createJob") : t("dispatcher.editJob")}
        </div>

        <div style={{ marginTop: 10 }}>
          <div className="job-label">{t("dispatcher.name")}</div>
          <input
            className="job-input"
            value={disDriverName}
            onChange={e => setDisDriverName(e.target.value)}
          />
        </div>

        <div style={{ marginTop: 10 }}>
          <div className="job-label">{t("commonLabel.phone")}</div>
          <input
            className="job-input"
            value={disDriverPhone}
            onChange={e => setDisDriverPhone(e.target.value)}
          />
        </div>

        <div style={{ marginTop: 10 }}>
          <div className="job-label">{t("dispatcher.disStatus")}</div>
          <select
            className="job-select"
            value={disStatus}
            onChange={e => setDisStatus(e.target.value)}
          >
            {DIS_STATUS.map(value => (
              <option key={value} value={value}>
                {t(`dispatcher.status.${value}`)}
              </option>
            ))}
          </select>
        </div>

        <div style={{ marginTop: 10 }}>
          <div className="job-label">{t("dispatcher.etaMins")}</div>
          <input
            className="job-input"
            type="number"
            value={disEtaMins}
            onChange={e => setDisEtaMins(e.target.value)}
          />
        </div>

        <div style={{ marginTop: 10 }}>
          <div className="job-label">{t("dispatcher.jobType")}</div>
          <select
            className="job-select"
            value={jobType}
            onChange={e => setJobType(e.target.value)}
          >
            {JOB_TYPE.map(value => (
              <option key={value} value={value}>
                {t(`dispatcher.jobTypes.${value}`)}
              </option>
            ))}
          </select>
        </div>

        <div style={{ marginTop: 10 }}>
          <div className="job-label">{t("dispatcher.jobSource")}</div>
          <input
            className="job-input"
            value={jobSource}
            onChange={e => setJobSource(e.target.value)}
          />
        </div>

        <div style={{ marginTop: 10 }}>
          <div className="job-label">{t("dispatcher.jobStatus")}</div>
          <input
            className="job-input"
            value={jobStatus}
            onChange={e => setJobStatus(e.target.value)}
          />
        </div>

        <div style={{ marginTop: 10 }}>
          <div className="job-label">{t("dispatcher.orderSummary")}</div>
          <input
            className="job-input"
            value={jobOrderSummary}
            onChange={e => setJobOrderSummary(e.target.value)}
          />
        </div>

        <div style={{ marginTop: 10 }}>
          <div className="job-label">{t("dispatcher.constraints")}</div>
          <textarea
            className="job-textarea"
            value={jobConstraints}
            onChange={e => setJobConstraints(e.target.value)}
          />
        </div>

        <div style={{ marginTop: 10 }}>
          <div className="job-label">{t("dispatcher.pricing")}</div>
          <textarea
            className="job-textarea"
            value={jobPricing}
            onChange={e => setJobPricing(e.target.value)}
          />
        </div>

        <div style={{ marginTop: 10 }}>
          <div className="job-label">{t("dispatcher.security")}</div>
          <textarea
            className="job-textarea"
            value={jobSecurity}
            onChange={e => setJobSecurity(e.target.value)}
          />
        </div>

        <div className="job-edit-row">
          <button className="btn-mini" onClick={cancelEditJob}>
            {t("commonLabel.cancel")}
          </button>
          <button className="btn-mini primary" disabled={savingJob} onClick={saveJob}>
            {t("commonLabel.save")}
          </button>
          {isCreateMode && (
            <button className="btn-mini primary" disabled={savingJob} onClick={handleAdd}>
              {t("commonLabel.add")}
            </button>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="view-page">
      <div className="view-card">
        <div className="view-header">
          <div>
            <h1 className="view-title">{t("dispatcher.title")}</h1>
            <p className="view-subtitle">{t("dispatcher.subtitle")}</p>
          </div>

          <div className="view-nav">
            <select
              className="view-dropdown"
              defaultValue=""
              onChange={e => {
                const route = e.target.value;
                if (route) window.location.href = route;
              }}
            >
              <option value="" disabled>{t("dropDown.dispatcher-page")}</option>
              <option value="/">{t("dropDown.home-page")}</option>
              <option value="/scheduler">{t("dropDown.scheduler-page")}</option>
              <option value="/clients">{t("dropDown.clients-page")}</option>
              <option value="/dashboard">{t("dropDown.dashboard-page")}</option>
              <option value="/crm-lite">{t("dropDown.crm-lite-page")}</option>
              <option value="/qr-code">{t("dropDown.qr-page")}</option>
              <option value="/commerce">{t("dropDown.commerce-page")}</option>
              <option value="/support">{t("dropDown.support-page")}</option>
              <option value="/analytics">{t("dropDown.analytics-page")}</option>
              <option value="/tutorial">{t("dropDown.tutorial-page")}</option>
            </select>
          </div>
        </div>

        <ClientSearch onSelect={handleClientSelect} />

        <div className="job-tools">
          <div className="job-tools-header">
            <div className="job-tools-title">{t("dispatcher.createJob")}</div>
            <button
              className="btn-link job-add-job"
              onClick={startCreateJob}
              disabled={savingJob}
              title={t("dispatcher.addJob")}
            >
              {t("dispatcher.addJob")}
            </button>
          </div>

          {isCreateMode && <div className="commonLabel.edit">{renderJobForm()}</div>}

          <div className="job-list">
            {paginatedJobs.map(dis => {
              const isEditingThis = isEditMode && editingDisJobs.assignment_id === dis.assignment_id;

              return (
                <React.Fragment key={dis.assignment_id}>
                  {isEditingThis ? (
                    <div className="commonLabel.edit">{renderJobForm()}</div>
                  ) : (
                    <div className="job-row">
                      <div className="job-item view-card" onClick={() => startEditJob(dis)}>
                        <div className="job-item-main">
                          <div className="job-item-title">
                            {t("dispatcher.orderSummary")}: {dis.order_summary || ""}
                          </div>
                          <div className="job-item-meta">
                            {t("dispatcher.name")}: {dis.driver_name || t("dispatcher.driverFallback", "Unassigned")}
                          </div>
                          <div className="job-item-meta">
                            {t("dispatcher.jobType")}: {getJobTypeLabel(dis.job_type) || "—"}
                          </div>
                          <div className="job-item-meta">
                            {t("commonLabel.phone")}: {dis.driver_phone || t("commonLabel.none", "—")}
                          </div>
                          <div className="job-item-meta">
                            {t("dispatcher.disStatus")}: {getJobStatusLabel(dis.dstatus || "unassigned")} -{" "}
                            {formatStatusDate(dis.jstatus, dis.created_at, dis.updated_at)}
                          </div>
                        </div>
                        <button
                          className="job-delete-btn side"
                          onClick={e => {
                            e.stopPropagation();
                            setJobToDelete(dis);
                          }}
                          title={t("commonLabel.delete")}
                        >
                          ×
                        </button>
                      </div>
                    </div>
                  )}
                </React.Fragment>
              );
            })}
          </div>

          {disJobs.length > DIS_PAGE_SIZE && (
            <div className="common-pagination">
              {hasPrevJobPage && (
                <button className="btn-link" onClick={() => setDisJobsPage(p => p - 1)}>
                  {t("commonLabel.previous")}
                </button>
              )}
              {hasNextJobPage && (
                <button className="btn-link" onClick={() => setDisJobsPage(p => p + 1)}>
                  {t("commonLabel.next")}
                </button>
              )}
            </div>
          )}
        </div>

        {jobToDelete && (
          <div className="confirm-overlay" onClick={() => setJobToDelete(null)}>
            <div className="confirm-modal" onClick={e => e.stopPropagation()}>
              <div className="confirm-title">{t("dispatcher.confirmDeleteTitle")}</div>
              <div className="confirm-text">
                {t("dispatcher.confirmDeleteMessage")} <strong>{jobToDelete.order_summary || jobToDelete.driver_name}</strong>?
              </div>
              <div className="confirm-actions">
                <button className="btn-mini" onClick={() => setJobToDelete(null)}>
                  {t("commonLabel.cancel")}
                </button>
                <button className="btn-mini primary" disabled={deletingJob} onClick={confirmDeleteJob}>
                  {t("commonLabel.delete")}
                </button>
              </div>
            </div>
          </div>
        )}

        {confirmOverwrite && (
          <div
            className="confirm-overlay"
            onClick={() => {
              setConfirmOverwrite(false);
              setPendingConfirmAction(null);
            }}
          >
            <div className="confirm-modal" onClick={e => e.stopPropagation()}>
              {(() => {
                const action = pendingConfirmAction || "save";
                const isAddConfirm = action === "add";
                return (
                  <>
                    <div className="confirm-title">
                      {t(isAddConfirm ? "dispatcher.createConfirmTitle" : "dispatcher.overwriteSaveTitle")}
                    </div>
                    <div className="confirm-text">
                      {t(isAddConfirm ? "dispatcher.createConfirmMessage" : "dispatcher.overwriteSaveMessage")}
                    </div>
                    <div className="confirm-actions">
                      <button
                        className="btn-mini"
                        onClick={() => {
                          setConfirmOverwrite(false);
                          setPendingConfirmAction(null);
                        }}
                      >
                        {t("commonLabel.cancel")}
                      </button>
                      <button
                        className="btn-mini primary"
                        onClick={async () => {
                          setConfirmOverwrite(false);
                          setPendingConfirmAction(null);
                          if (action === "add") {
                            setDisStatus("assigned");
                            setJobOrderSummary("");
                            await performSave({ mode: "add" });
                            return;
                          }
                          await performSave({ mode: "save" });
                        }}
                      >
                        {t(isAddConfirm ? "dispatcher.create" : "dispatcher.overwrite")}
                      </button>
                    </div>
                  </>
                );
              })()}
            </div>
          </div>
        )}

        {validationMessage && (
          <div className="confirm-overlay" onClick={() => setValidationMessage("")}>
            <div className="confirm-modal" onClick={e => e.stopPropagation()}>
              <div className="confirm-title">{t("dispatcher.validationTitle")}</div>
              <div className="confirm-text">{validationMessage}</div>
              <div className="confirm-actions">
                <button
                  className="btn-mini primary"
                  onClick={() => {
                    if (!hasPlatformAccess && selectedClient) {
                      window.location.href = "/dashboard?tab=onboarding";
                    }
                    setValidationMessage("");
                  }}
                >
                  {t("commonLabel.ok")}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}